// Pen (spec §5.5): collect coalesced pointer positions, preview the raw
// stroke, and on release store one freehand object with simplified points.
import type { Freehand } from '../../shared/types.ts';
import { newId } from '../../shared/ids.ts';
import type { Editor } from '../editor.ts';
import { buildStroke } from '../engine/freehand.ts';
import type { Tool, WorldPointerEvent } from './types.ts';

const TOLERANCE_PX = 0.5;   // simplification tolerance at the drawing zoom (spec §5.5)
const MIN_STEP_PX = 0.5;    // ignore sub-pixel jitter while collecting

export class PenTool implements Tool {
  cursor = 'crosshair';
  private readonly ed: Editor;
  private points: number[] | null = null;

  constructor(editor: Editor) {
    this.ed = editor;
  }

  onPointerDown(e: WorldPointerEvent): void {
    this.points = [e.x, e.y];
  }

  onPointerMove(e: WorldPointerEvent): void {
    const pts = this.points;
    if (!pts) return;
    const min = MIN_STEP_PX / this.ed.camera.zoom;
    for (const [x, y] of e.coalesced.length ? e.coalesced : [[e.x, e.y] as [number, number]]) {
      const lx = pts[pts.length - 2]!, ly = pts[pts.length - 1]!;
      if (Math.abs(x - lx) + Math.abs(y - ly) >= min) pts.push(x, y);
    }
    this.ed.renderer.invalidateInteractive();
  }

  onPointerUp(): void {
    const pts = this.points;
    this.points = null;
    if (!pts) return;
    const ed = this.ed;
    const width = ed.penWidth;
    const stroke = buildStroke(pts, TOLERANCE_PX / ed.camera.zoom, width);
    const obj: Freehand = {
      id: newId(), type: 'freehand', ...stroke, rotation: 0,
      z: ed.zKeysOnTop(1)[0]!, createdBy: ed.userId, stroke: ed.penColor, strokeWidth: width,
    };
    ed.apply([{ t: 'create', obj }]);
    ed.renderer.invalidateInteractive();
  }

  onCancel(): void {
    this.points = null;
    this.ed.renderer.invalidateInteractive();
  }

  /** The stroke being drawn, before simplification. */
  drawOverlay(ctx: CanvasRenderingContext2D): void {
    const pts = this.points;
    if (!pts) return;
    ctx.beginPath();
    ctx.moveTo(pts[0]!, pts[1]!);
    for (let i = 2; i < pts.length; i += 2) ctx.lineTo(pts[i]!, pts[i + 1]!);
    if (pts.length === 2) ctx.lineTo(pts[0]! + 0.01, pts[1]!);
    ctx.strokeStyle = this.ed.penColor;
    ctx.lineWidth = this.ed.penWidth;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.stroke();
  }
}
